import { Avatar, Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { PhoneOutlined as PhoneIcon } from "@mui/icons-material";

const getInitials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

export const getFirstName = (member) => member?.name?.split(/\s+/)[0] || "";

// tel: links only want digits and a leading +. No digits means no link.
const toTelHref = (phone) => {
  const number = (phone || "").replace(/[^\d+]/g, "");
  return /\d/.test(number) ? `tel:${number}` : null;
};

/**
 * "Hosted by" style intro to the team member looking after the quote, built to
 * sit inside the progress sidebar rather than as a card of its own.
 * Avatar shows initials when there is no photo or the photo fails to load.
 */
const TeamMemberCard = ({ member, mainColor, accentColor, compact = false }) => {
  if (!member?.name) return null;

  const firstName = getFirstName(member);
  const callHref = toTelHref(member.phoneNumber);

  const avatar = (size) => (
    <Avatar
      src={member.photoUrl || undefined}
      alt={member.name}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        fontWeight: 700,
        backgroundColor: mainColor,
        color: "white",
        flexShrink: 0,
        // Thin gold ring, matching the page's accent dividers.
        boxShadow: `0 0 0 2px white, 0 0 0 3.5px ${accentColor}`,
      }}
    >
      {getInitials(member.name)}
    </Avatar>
  );

  const overline = (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: accentColor,
        fontWeight: 700,
        letterSpacing: 1.5,
        textTransform: "uppercase",
        fontSize: "0.65rem",
        lineHeight: 1.4,
      }}
    >
      Your event host
    </Typography>
  );

  if (compact) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%", px: 0.5 }}>
        {avatar(44)}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {overline}
          <Typography variant="body2" noWrap sx={{ fontWeight: 700, color: mainColor }}>
            {member.name}
            {member.jobTitle && (
              <Box component="span" sx={{ fontWeight: 400, color: "#666" }}>
                {" "}· {member.jobTitle}
              </Box>
            )}
          </Typography>
        </Box>
        {callHref && (
          <Tooltip title={`Call ${firstName}`}>
            <IconButton
              href={callHref}
              aria-label={`Call ${firstName}`}
              sx={{ color: mainColor, backgroundColor: "rgba(56,0,69,0.05)" }}
            >
              <PhoneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {avatar(56)}
        <Box sx={{ minWidth: 0 }}>
          {overline}
          <Typography variant="body1" sx={{ fontWeight: 700, color: mainColor, lineHeight: 1.3 }}>
            {member.name}
          </Typography>
          {member.jobTitle && (
            <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
              {member.jobTitle}
            </Typography>
          )}
        </Box>
      </Box>
      {member.bio && (
        <Typography
          variant="body2"
          sx={{ color: "#475569", mt: 1.5, lineHeight: 1.6, fontSize: "0.82rem" }}
        >
          {member.bio}
        </Typography>
      )}
      {callHref && (
        <Button
          size="small"
          startIcon={<PhoneIcon />}
          href={callHref}
          sx={{
            mt: 1,
            ml: -1,
            textTransform: "none",
            fontWeight: 600,
            color: mainColor,
            "&:hover": { backgroundColor: "rgba(56,0,69,0.05)" },
          }}
        >
          Call {firstName}
        </Button>
      )}
    </Box>
  );
};

export default TeamMemberCard;
